import React, { useState, useEffect } from "react";
import { DollarSign, Heart, Activity, ShoppingBag } from "lucide-react";
import {
  categoriesService,
  subcategoriesService,
  Category as SupabaseCategory,
  Subcategory as SupabaseSubcategory,
} from "@/lib/supabase";

export interface Category {
  icon: React.ReactNode;
  title: string;
  description: string;
  path: string;
  longDescription: string;
}

export interface Subcategory {
  label: string;
  value: string;
  description?: string;
}

export interface CategoryWithSubcategories {
  categoryName: string;
  slug: string;
  path: string;
  subcategories: Subcategory[];
}

export const useCategories = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryMapping, setCategoryMapping] = useState<
    Record<string, CategoryWithSubcategories>
  >({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Descriptions cho các danh mục
  const descriptions: Record<string, { short: string; long: string }> = {
    "smart-living": {
      short:
        "Elevate your everyday life with shopping tips, finance guides, and home organization strategies.",
      long: "Master the art of smart spending, discover innovative home organization solutions, and learn practical financial management tips tailored for modern women.",
    },
    beauty: {
      short:
        "Unlock your radiance with expert skincare routines, makeup trends, and fashion guidance.",
      long: "Explore curated beauty tips, stay updated with the latest fashion trends, and discover personalized skincare routines that enhance your natural beauty.",
    },
    health: {
      short:
        "Balance, nurture & thrive with nutrition knowledge, exercise routines, and family wellness tips.",
      long: "Find the perfect balance between personal wellness and family care with expert advice on nutrition, fitness, and creating harmonious family relationships.",
    },
    shop: {
      short:
        "Shop smart, live chic with product reviews, deal alerts, and exclusive discounts from top brands.",
      long: "Make informed purchasing decisions with our detailed product reviews, exclusive deals, and insider tips on getting the best value for your lifestyle needs.",
    },
  };

  // Map các icon dựa vào tên danh mục
  const getCategoryIcon = (slug: string) => {
    switch (slug) {
      case "smart-living":
        return <DollarSign className="h-8 w-8" />;
      case "beauty":
        return <Heart className="h-8 w-8" />;
      case "health":
        return <Activity className="h-8 w-8" />;
      case "shop":
        return <ShoppingBag className="h-8 w-8" />;
      default:
        return <DollarSign className="h-8 w-8" />;
    }
  };

  useEffect(() => {
    const fetchCategoriesAndSubcategories = async () => {
      setIsLoading(true);
      try {
        // Fetch categories from Supabase
        const categoriesData = await categoriesService.getAll();
        const allSubcategories = await subcategoriesService.getAll();

        // Create the main categories array for UI display
        const mappedCategories = categoriesData.map((category) => ({
          icon: getCategoryIcon(category.slug),
          title: category.name,
          description:
            descriptions[category.slug]?.short ||
            `Explore our ${category.name} articles and resources.`,
          longDescription:
            descriptions[category.slug]?.long ||
            `Discover a wealth of information and insights in our ${category.name} section.`,
          path: `/articles?category=${category.slug}`,
        }));

        // Create a comprehensive mapping with subcategories for helper functions
        const categoryMappingObj: Record<string, CategoryWithSubcategories> =
          {};

        for (const category of categoriesData) {
          // Get subcategories for this category
          const categorySubcategories = allSubcategories
            .filter((sub) => sub.category_id === category.id)
            .map((sub) => ({
              label: sub.name,
              value: sub.slug,
              description: `Explore ${sub.name} content and resources.`,
            }));

          categoryMappingObj[category.slug] = {
            categoryName: category.name,
            slug: category.slug,
            path: `/articles?category=${category.slug}`,
            subcategories: categorySubcategories,
          };
        }

        setCategories(mappedCategories);
        setCategoryMapping(categoryMappingObj);
        setError(null);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setError("Failed to load categories");
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategoriesAndSubcategories();
  }, []);

  const categoryNames = ["All", ...categories.map((cat) => cat.title)];
  const categorySubcategories = Object.values(categoryMapping);

  // Get category by slug
  const getCategoryBySlug = (
    slug: string
  ): CategoryWithSubcategories | undefined => {
    return categoryMapping[slug];
  };

  // Get category by name
  const getCategoryByName = (
    name: string
  ): CategoryWithSubcategories | undefined => {
    return Object.values(categoryMapping).find(
      (cat) => cat.categoryName === name
    );
  };

  // Get subcategories for a specific category (by name or slug)
  const getCategorySubcategories = (
    categoryIdentifier: string
  ): Subcategory[] => {
    // First try by slug
    let category = getCategoryBySlug(categoryIdentifier);

    // If not found, try by name
    if (!category) {
      category = getCategoryByName(categoryIdentifier);
    }

    return category ? category.subcategories : [];
  };

  // Get slug for a category name
  const getSlugForCategoryName = (categoryName: string): string => {
    const category = getCategoryByName(categoryName);
    return category
      ? category.slug
      : categoryName.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and");
  };

  // Get category name for a slug
  const getCategoryNameForSlug = (slug: string): string => {
    const category = getCategoryBySlug(slug);
    return category ? category.categoryName : "All";
  };

  // Get category slugs
  const getCategorySlugs = (): string[] => {
    return ["all", ...Object.keys(categoryMapping)];
  };

  // Get full category structure
  const getCategoryStructure = (): CategoryWithSubcategories[] => {
    return categorySubcategories;
  };

  return {
    categories,
    isLoading,
    error,
    categoryNames,
    getCategorySubcategories,
    getCategoryBySlug,
    getCategoryByName,
    getSlugForCategoryName,
    getCategoryNameForSlug,
    getCategorySlugs,
    getCategoryStructure,
    categorySubcategories,
  };
};
